import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save, Plus, Trash2, Loader2, Settings as SettingsIcon, ListOrdered } from 'lucide-react'
import { z } from 'zod'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { NumberInput } from '@/components/ui/NumberInput'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { Toggle } from '@/components/ui/Toggle'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { useZodForm } from '@/hooks/useZodForm'
import { useAuthority } from '@/hooks/useAuthority'
import { applyApiErrorsToForm } from '@/lib/api-errors'
import { onValidationFail } from '@/lib/form-helpers'
import {
  CredentialingCommissionService,
  type CredentialingCommissionStructure,
} from '@/services/modules/CredentialingService'
import { toast } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'

const formSchema = z.object({
  name: z.string().trim().min(1, 'Informe um nome para o modelo de comissão.').max(150, 'O nome não pode passar de 150 caracteres.'),
  goal: z.number().int().min(0, 'A meta não pode ser negativa.').nullable(),
  noGoal: z.boolean(),
  status: z.enum(['ATIVO', 'INATIVO']),
}).superRefine((val, ctx) => {
  if (!val.noGoal && (val.goal === null || val.goal < 0)) {
    ctx.addIssue({
      code: 'custom',
      path: ['goal'],
      message: 'Informe a meta de vendas mensais ou marque "Sem meta" para deixar livre.',
    })
  }
})
type FormValues = z.infer<typeof formSchema>

const ruleSchema = z.object({
  contractAction: z.enum(['INTERVALO', 'A PARTIR DE']),
  contractFrom: z.number({ message: 'Informe o número do contrato inicial.' }).int().min(1, 'O contrato inicial deve ser pelo menos 1.'),
  contractTo: z.number().int().min(1).nullable(),
  value: z.number({ message: 'Informe o valor da comissão.' }).min(0, 'O valor da comissão não pode ser negativo.'),
}).superRefine((val, ctx) => {
  if (val.contractAction === 'INTERVALO') {
    if (val.contractTo === null || val.contractTo === undefined) {
      ctx.addIssue({ code: 'custom', path: ['contractTo'], message: 'Informe o número do contrato final do intervalo.' })
    } else if (val.contractTo < val.contractFrom) {
      ctx.addIssue({ code: 'custom', path: ['contractTo'], message: 'O contrato final deve ser igual ou maior que o inicial.' })
    }
  }
})
type RuleValues = z.infer<typeof ruleSchema>

interface DraftRule extends RuleValues {
  draftId: string
  id?: string
}

export function CredentialingCommissionStructureFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = !!id
  const [loading, setLoading] = useState(isEdit)
  const [activeSection, setActiveSection] = useState('basic')
  const { can } = useAuthority()
  // Regras (adicionar/remover) têm permissão própria no backend. Na criação as
  // regras são enviadas logo após o POST da tabela, então salvar exige as duas.
  const canManageRules = can('credentialing.commission_structures.rules')
  const canSave = isEdit
    ? can('credentialing.commission_structures.edit')
    : can('credentialing.commission_structures.create') && canManageRules

  const form = useZodForm(formSchema, {
    // Switcher "Sem meta" começa DESLIGADO (com meta ativa por padrão).
    defaultValues: { name: '', goal: 0, noGoal: false, status: 'ATIVO' },
  })
  const noGoal = form.watch('noGoal')

  const [rules, setRules] = useState<DraftRule[]>([])
  const [rulesError, setRulesError] = useState<string | null>(null)
  const [deletingRule, setDeletingRule] = useState<DraftRule | null>(null)

  const ruleForm = useForm<RuleValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(ruleSchema),
    defaultValues: {
      contractAction: 'INTERVALO',
      contractFrom: 1,
      contractTo: 5,
      value: 0,
    },
  })
  const ruleAction = ruleForm.watch('contractAction')

  useEffect(() => {
    if (!isEdit || !id) return
    setLoading(true)
    CredentialingCommissionService.show(id)
      .then((s: CredentialingCommissionStructure) => {
        form.reset({
          name: s.name,
          goal: s.monthlyContractSalesGoal,
          noGoal: s.monthlyContractSalesGoal == null,
          status: s.status,
        })
        setRules(
          (s.rules ?? [])
            .map((r): DraftRule => ({
              draftId: r.id, id: r.id,
              contractFrom: r.contractFrom,
              contractTo: r.contractTo,
              contractAction: r.contractAction,
              value: r.value,
            }))
            .sort((a, b) => a.contractFrom - b.contractFrom),
        )
      })
      .catch(() => toast.error('Tabela não encontrada.'))
      .finally(() => setLoading(false))
  }, [isEdit, id, form])

  useEffect(() => {
    if (ruleAction === 'A PARTIR DE') ruleForm.setValue('contractTo', null, { shouldValidate: false })
  }, [ruleAction, ruleForm])

  // Quando "Sem meta" liga, zera o valor da meta.
  useEffect(() => {
    if (noGoal) form.setValue('goal', null, { shouldValidate: false })
  }, [noGoal, form])

  const sortedRules = useMemo(
    () => [...rules].sort((a, b) => a.contractFrom - b.contractFrom),
    [rules],
  )

  async function onAddRule(values: RuleValues) {
    const draft: DraftRule = {
      ...values,
      draftId: crypto.randomUUID(),
      contractTo: values.contractAction === 'A PARTIR DE' ? null : values.contractTo,
    }

    // Validação de sobreposição com regras existentes.
    const overlap = findOverlap(draft, rules)
    if (overlap) {
      const conflictDesc = describeRangeContract(overlap)
      ruleForm.setError('contractFrom', { type: 'overlap', message: `Sobrepõe a regra existente: ${conflictDesc}.` })
      if (draft.contractAction === 'INTERVALO') {
        ruleForm.setError('contractTo', { type: 'overlap', message: `Sobrepõe a regra existente: ${conflictDesc}.` })
      }
      toast.error({
        title: 'Faixa de contratos em conflito',
        description: `Essa nova regra sobrepõe uma regra já cadastrada (${conflictDesc}). Ajuste o intervalo para não se sobrepor.`,
      })
      return
    }

    if (isEdit && id) {
      try {
        const r = await CredentialingCommissionService.createRule(id, {
          contractFrom: draft.contractFrom,
          contractTo: draft.contractTo,
          contractAction: draft.contractAction,
          value: draft.value,
          commissionType: 'VALOR FIXO',
        })
        draft.id = r.id
      } catch (e) {
        applyApiErrorsToForm(e, ruleForm.setError)
        return
      }
    }
    setRules((p) => [...p, draft])
    setRulesError(null)
    ruleForm.setValue('value', 0)
    ruleForm.clearErrors()
    toast.success({ title: 'Regra adicionada', description: 'A nova regra foi incluída no modelo.' })
  }

  async function confirmDelRule() {
    if (!deletingRule) return
    if (isEdit && id && deletingRule.id) {
      try {
        await CredentialingCommissionService.destroyRule(id, deletingRule.id)
      } catch (e) {
        applyApiErrorsToForm(e, ruleForm.setError)
        return
      }
    }
    setRules((p) => p.filter((x) => x.draftId !== deletingRule.draftId))
    setDeletingRule(null)
    toast.success({ title: 'Regra removida', description: 'A regra foi excluída do modelo.' })
  }

  async function onSubmit(values: FormValues) {
    if (!canSave) return
    if (rules.length === 0) {
      setRulesError('É preciso cadastrar pelo menos uma regra de comissionamento.')
      setActiveSection('rules')
      toast.error({
        title: 'Faltam dados',
        description: 'Cadastre pelo menos uma regra de comissionamento antes de salvar.',
      })
      return
    }
    try {
      const payload = {
        name: values.name,
        monthly_contract_sales_goal: values.noGoal ? null : values.goal,
        status: values.status,
      }
      let savedId = id
      if (isEdit && id) {
        await CredentialingCommissionService.update(id, payload)
      } else {
        const created = await CredentialingCommissionService.create(payload)
        savedId = created.id
        for (const r of rules) {
          await CredentialingCommissionService.createRule(savedId, {
            contractFrom: r.contractFrom,
            contractTo: r.contractTo,
            contractAction: r.contractAction,
            value: r.value,
            commissionType: 'VALOR FIXO',
          })
        }
      }
      toast.success({
        title: isEdit ? 'Modelo atualizado' : 'Modelo criado',
        description: isEdit
          ? 'As alterações foram salvas com sucesso.'
          : 'O novo modelo de comissão foi criado.',
      })
      // REGRA GLOBAL: após criar/atualizar, sempre volta para a listagem.
      navigate('/settings/credentialing/commission-structures')
    } catch (e) {
      applyApiErrorsToForm(e, form.setError, {
        monthly_contract_sales_goal: 'goal',
      })
    }
  }

  const basicErrorCount =
    (form.formState.errors.name ? 1 : 0) +
    (form.formState.errors.goal ? 1 : 0)
  const rulesErrorCount = rulesError ? 1 : 0

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-gray-400" />
      </div>
    )
  }

  const basicSection = (
    <SectionShell title="Dados do modelo" description="Comissionamento por contratos credenciados.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Nome do modelo de comissão"
          required
          wrapperClassName="md:col-span-2"
          {...form.register('name')}
          error={form.formState.errors.name?.message}
        />

        <Controller
          name="goal"
          control={form.control}
          render={({ field, fieldState }) => (
            <FieldWithSwitch
              label="Meta de Vendas Mensais"
              required={!noGoal}
              disabled={noGoal}
              switchLabel="Sem meta"
              switchValue={noGoal}
              onSwitchChange={(v) => form.setValue('noGoal', v, { shouldValidate: true })}
            >
              <NumberInput
                value={field.value}
                onChange={field.onChange}
                disabled={noGoal}
                placeholder={noGoal ? '— Sem meta —' : '0'}
                min={0}
                error={fieldState.error?.message}
              />
            </FieldWithSwitch>
          )}
        />

        <Controller
          name="status"
          control={form.control}
          render={({ field }) => (
            <div className="md:col-span-2">
              <StatusToggle value={field.value} onChange={field.onChange} />
            </div>
          )}
        />
      </div>
    </SectionShell>
  )

  const rulesSection = (
    <SectionShell title="Regras de comissionamento" description="Faixas de contratos fechados × valor de comissão. É obrigatório cadastrar pelo menos uma regra.">
      {rulesError && (
        <div className="mb-4 px-3 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-[13px] text-rose-700">
          {rulesError}
        </div>
      )}

      {/* NÃO usa <form> aqui (form aninhado no form principal não é permitido).
          Submit disparado manualmente via ruleForm.handleSubmit no botão. */}
      {canManageRules && (
      <div className="bg-gray-50 ring-1 ring-gray-200 rounded-md p-4 mb-4">
        <div className={cn('grid grid-cols-1 gap-3 items-start', ruleAction === 'A PARTIR DE' ? 'md:grid-cols-3' : 'md:grid-cols-4')}>
          <Controller
            name="contractAction"
            control={ruleForm.control}
            render={({ field }) => (
              <Select
                label="Tipo"
                required
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                options={[
                  { value: 'INTERVALO',    label: 'Intervalo' },
                  { value: 'A PARTIR DE',  label: 'A partir de' },
                ]}
                searchable={false}
                clearable={false}
              />
            )}
          />
          <Controller
            name="contractFrom"
            control={ruleForm.control}
            render={({ field, fieldState }) => (
              <NumberInput label="Contrato de" required value={field.value ?? null} onChange={field.onChange} min={1} error={fieldState.error?.message} />
            )}
          />
          {ruleAction !== 'A PARTIR DE' && (
            <Controller
              name="contractTo"
              control={ruleForm.control}
              render={({ field, fieldState }) => (
                <NumberInput label="Contrato até" required value={field.value ?? null} onChange={field.onChange} min={1} error={fieldState.error?.message} />
              )}
            />
          )}
          <Controller
            name="value"
            control={ruleForm.control}
            render={({ field, fieldState }) => (
              <CurrencyInput label="Valor" required value={field.value ?? null} onChange={field.onChange} error={fieldState.error?.message} />
            )}
          />
        </div>
        <div className="mt-3 text-right">
          <Button
            type="button"
            onClick={ruleForm.handleSubmit(onAddRule, onValidationFail)}
            loading={ruleForm.formState.isSubmitting}
            iconLeft={<Plus size={12} />}
          >
            Adicionar regra
          </Button>
        </div>
      </div>
      )}

      <div className="overflow-x-auto bg-white rounded-md ring-1 ring-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left   text-[11px] font-semibold uppercase tracking-wider text-gray-500">Tipo</th>
              <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-500">Faixa de contratos</th>
              <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-500">Valor</th>
              <th className="px-2 w-12" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedRules.length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400">Nenhuma regra cadastrada.</td></tr>
            ) : sortedRules.map((r) => (
              <tr key={r.draftId}>
                <td className="px-3 py-2 text-xs text-gray-700">
                  {r.contractAction === 'A PARTIR DE' ? 'A partir de' : 'Intervalo'}
                </td>
                <td className="px-3 py-2 text-xs text-center">
                  {r.contractAction === 'A PARTIR DE'
                    ? `A partir do contrato ${r.contractFrom}`
                    : `Contratos ${r.contractFrom} – ${r.contractTo}`}
                </td>
                <td className="px-3 py-2 text-center">R$ {r.value.toFixed(2).replace('.', ',')}</td>
                <td className="px-2 py-2 text-right">
                  {canManageRules && (
                  <button
                    type="button"
                    onClick={() => setDeletingRule(r)}
                    aria-label="Remover regra"
                    className="cursor-pointer inline-flex items-center justify-center w-8 h-8 rounded-md text-rose-500 hover:bg-rose-50 transition"
                  >
                    <Trash2 size={14} />
                  </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionShell>
  )

  const sections: FormSection[] = [
    { key: 'basic', label: 'Dados do modelo',           icon: SettingsIcon, content: basicSection, hasError: basicErrorCount > 0, errorCount: basicErrorCount },
    { key: 'rules', label: 'Regras de comissionamento', icon: ListOrdered,  content: rulesSection, hasError: rulesErrorCount > 0, errorCount: rulesErrorCount },
  ]

  return (
    <form onSubmit={form.handleSubmit(onSubmit, (errors) => { setActiveSection('basic'); onValidationFail(errors) })}>
      <PageHeader
        title={isEdit ? 'Editar modelo de comissão' : 'Novo modelo de comissão'}
        subtitle="Comissionamento de credenciamento"
        crumbs={[
          { label: 'Configurações', to: '/settings' },
          { label: 'Credenciamento' },
          { label: 'Comissionamento', to: '/settings/credentialing/commission-structures' },
          { label: isEdit ? 'Editar' : 'Novo' },
        ]}
        backTo="/settings/credentialing/commission-structures"
        actions={
          <Button
            type="submit"
            loading={form.formState.isSubmitting}
            iconLeft={<Save size={14} />}
            disabled={!canSave}
            title={canSave ? undefined : `Sem permissão para ${isEdit ? 'editar' : 'criar'} tabelas de comissão`}
          >
            Salvar
          </Button>
        }
      />

      <FormSidemenu sections={sections} activeKey={activeSection} onChange={setActiveSection} />

      <ConfirmDialog
        open={!!deletingRule}
        onClose={() => setDeletingRule(null)}
        onConfirm={confirmDelRule}
        title="Remover regra?"
        description={deletingRule
          ? `Remover regra ${deletingRule.contractAction === 'A PARTIR DE'
              ? `'a partir do contrato ${deletingRule.contractFrom}'`
              : `'contratos ${deletingRule.contractFrom}–${deletingRule.contractTo}'`}? Esta ação não pode ser desfeita.`
          : ''}
        confirmLabel="Remover"
        variant="danger"
      />
    </form>
  )
}

// Faixa numérica [from, to]. "A PARTIR DE" usa Infinity como to.
function rangeOf(r: { contractAction: 'INTERVALO' | 'A PARTIR DE'; contractFrom: number; contractTo: number | null }) {
  return {
    from: r.contractFrom,
    to: r.contractAction === 'A PARTIR DE' ? Number.POSITIVE_INFINITY : (r.contractTo ?? r.contractFrom),
  }
}

/** Primeira regra que sobrepõe `draft`, ou null. */
function findOverlap(draft: DraftRule, existing: DraftRule[]): DraftRule | null {
  const a = rangeOf(draft)
  for (const r of existing) {
    if (r.draftId === draft.draftId) continue
    const b = rangeOf(r)
    if (a.from <= b.to && b.from <= a.to) return r
  }
  return null
}

function describeRangeContract(r: DraftRule): string {
  return r.contractAction === 'A PARTIR DE'
    ? `a partir do contrato ${r.contractFrom}`
    : `contratos ${r.contractFrom} a ${r.contractTo}`
}

function SectionShell({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  )
}

function FieldWithSwitch({
  label, required, switchLabel, switchValue, onSwitchChange, disabled, children,
}: {
  label: string
  required?: boolean
  switchLabel: string
  switchValue: boolean
  onSwitchChange: (v: boolean) => void
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-1">
        <label className={cn('text-xs font-medium', disabled ? 'text-gray-400' : 'text-gray-700')}>
          {label}
          {required && <span className="text-rose-500 ml-0.5">*</span>}
        </label>
        <Toggle checked={switchValue} onChange={onSwitchChange} label={switchLabel} size="sm" labelSize="sm" />
      </div>
      {children}
    </div>
  )
}
